import React, { useState, useEffect, useRef } from "react";
import ReactMapboxGl, { Layer, Source, Marker, GeoJSONLayer, Popup } from 'react-mapbox-gl';
import moment from "moment";
import axios from "axios";
import Mapbox from "mapbox-gl";
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_DEFAULT_CENTER_CORDINATES, MAPBOX_DEFAULT_MIN_ZOOM, MAPBOX_DEFAULT_ZOOM, MAPBOX_SATELLITE_STREETS_STYLE, MAPBOX_STREETS_STYLE } from "../../constants/app";
import { NEW_API_URL, MAPBOX_GEOCODING_API } from "../../config/api";
import { MAPBOX_ACCESS_TOKEN } from "../../config/mapbox";
import MapSearch from "./components/MapSearch/MapSearch";
import FireRiskAlert from "./components/FireRiskAlert/FireRiskAlert";
import LoginAlert from "./components/LoginAlert/LoginAlert";
import { MapLegend } from "./components/MapLegend/MapLegend";
// import RangeSlider from "./components/RangeSlider/RangeSlider";
import Modal from "./components/Modal/Modal";
import mapboxgl from 'mapbox-gl';
import SwitchControl from "./components/SwitchControl/SwitchControl";
import { firestore as db } from "../../config/Firebase";
import TimeSeriesChart from "./components/TimeSeriesChart/TimeSeriesChart";
import layersEnum from "../../utils/enums/layers";
import FireMarker from "./components/FireMarker/FireMarker";
import FirePopupContent from "./components/FirePopupContent/FirePopupContent";
import { fetchFireRiskForPoint, fetchActiveFires, fetchTimeseriesFireRisk } from "../../services/fireRiskService";

// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

const Map = ReactMapboxGl({
	accessToken: MAPBOX_ACCESS_TOKEN,
	preserveDrawingBuffer: true,
	minZoom: 4.10
});

const MapboxMap = () => {
	const startDate = moment().format("YYYY/M/D");
	const [raster, setRaster] = useState(`${NEW_API_URL}firerisk/${startDate}/tiles/{z}/{x}/{y}.png?scale=1&TileMatrixSetId=WebMercatorQuad&resampling_method=nearest&return_mask=true`);
	const [activeFiresGeoJson, setActiveFiresGeoJson] = useState(null);
	const [activeLayers, setActiveLayers] = useState([layersEnum.wildFireAi.value, layersEnum.activeFire.value]);
	const [modalIsVisible, setModalIsVisible] = useState(false);
	const [fireRiskValue, setFireRiskValue] = useState(0);
	const [timeseriesFireRiskData, setTimeseriesFireRiskData] = useState({});
	const [selectedDate, setSelectedDate] = useState(null);
	const [selectedAddress, setSelectedAddress] = useState(null);
	const [popup, setPopup] = useState(null);
	const [error, setError] = useState("");
	const mapRef = useRef();

	const [mapCenter, setMapCenter] = useState(MAPBOX_DEFAULT_CENTER_CORDINATES);
	const [markerCoords, setMarketCoords] = useState([]);
	const [mapboxMapStyle, setMapboxMapStyle] = useState(MAPBOX_STREETS_STYLE);
	const [currentZoom, setCurrentZoom] = useState(MAPBOX_DEFAULT_ZOOM);
	const [switchControlContent, setSwitchControlContent] = useState({
		backgroundImg: 'ctrl_satellite_background.png',
		text: 'Satellite'
	});
	const [isTimeseriesHidden, setIsTimeseriesHidden] = useState(true);

	useEffect(() => {
		fetchActiveFires().then((data) => {
			setActiveFiresGeoJson(data);
		});
	}, [])


	const RASTER_SOURCE_OPTIONS = {
		"type": "raster",
		"tiles": [
			raster
		],
		"tileSize": 512,
	};

	const flyTo = (coords) => {
		const { map } = mapRef.current.state;

		if (coords.bbox) {
			map.fitBounds(coords.bbox);
		} else {
			setMapCenter(coords.center);
			setCurrentZoom(12);
			setMarketCoords(coords.center);
		}

		const date = selectedDate ? selectedDate : moment().format("YYYY/MM/DD");
		const [lng, lat] = coords.geometry.coordinates;

		fetchFireRiskForPoint({ date, lat, lng })
			.then((value) => {
				setFireRiskValue(value);
			})
	};

	const onMapClick = (e, map) => {
		setMarketCoords([map.lngLat.lng, map.lngLat.lat]);

		const { lat, lng } = map.lngLat;
		const date = selectedDate ? selectedDate : moment().format("YYYY/MM/DD");

		// Fetch the fire risk from the BE
		fetchFireRiskForPoint({ date, lat, lng })
			.then((value) => {
				setFireRiskValue(value);
			});

		//Fetch timeseries fire risk
		fetchTimeseriesFireRisk({ date, lng, lat })
			.then(timeseriesFireRiskData => {
				setTimeseriesFireRiskData(timeseriesFireRiskData);
				setIsTimeseriesHidden(false);
			})

		// Derrive the address from the point
		// TODO: add this as service
		axios.get(`${MAPBOX_GEOCODING_API}${lng},${lat}.json?access_token=${MAPBOX_ACCESS_TOKEN}`)
			.then((response) => {
				const features = response.data.features;
				const address = features.find((item) => item.place_type[0] === "address");
				const neighborhood = features.find((item) => item.place_type[0] === "neighborhood");
				const place = features.find((item) => item.place_type[0] === "place");

				if (address?.place_name) {
					setSelectedAddress(address.place_name);
				} else if (neighborhood?.place_name) {
					setSelectedAddress(neighborhood.place_name);
				} else if (place?.place_name) {
					setSelectedAddress(place.place_name);
				}
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const onStyleLoad = (map) => {
		// disable map rotation using right click + drag 
		map.dragRotate.disable();

		// disable map rotation using touch rotation gesture
		map.touchZoomRotate.disableRotation();

		const fullscreenControl = new Mapbox.FullscreenControl();
		const navigationControl = new Mapbox.NavigationControl();
		const scaleControl = new Mapbox.ScaleControl();
		map.addControl(fullscreenControl);
		map.addControl(navigationControl);
		map.addControl(scaleControl);
	}

	const onSavePoint = (event, email) => {
		event.preventDefault();

		if (!email) {
			setError("Email is required");
			return;
		};

		const re = /\S+@\S+\.\S+/;

		if (!re.test(email)) {
			setError("Email is not valid");
			return;
		}

		db.collection("subscribers")
			.add({
				email,
				coordinates: markerCoords
			})
			.then(() => {
				setModalIsVisible(false);
			});
	};

	const setVisibilityToModal = () => {
		if (markerCoords.length) {
			setModalIsVisible(!modalIsVisible);
		}
	};

	const onZoom = (map) => {
		console.log(map.getZoom());
		setCurrentZoom(map.getZoom());
	}

	const changeMapStyle = () => {
		if (mapboxMapStyle === MAPBOX_STREETS_STYLE) {
			setMapboxMapStyle(MAPBOX_SATELLITE_STREETS_STYLE);
			setSwitchControlContent({
				backgroundImg: '/ctrl_streets_background.png',
				text: 'Map'
			})
		} else {
			setMapboxMapStyle(MAPBOX_STREETS_STYLE);
			setSwitchControlContent({
				backgroundImg: '/ctrl_satellite_background.png',
				text: 'Satellite'
			})
		}
	}

	const onTimeseriesPointClick = (date) => {
		const formattedDate = moment(date).format('YYYY/MM/DD');
		const [lng, lat] = markerCoords;

		setRaster(
			`${NEW_API_URL}firerisk/${formattedDate}/tiles/{z}/{x}/{y}.png?scale=1&TileMatrixSetId=WebMercatorQuad&resampling_method=nearest&return_mask=true`
		);

		fetchFireRiskForPoint({ date: formattedDate, lng, lat })
			.then(fireRisk => {
				setFireRiskValue(fireRisk);
			})
	};

	const handleLayerChange = (newLayers) => {
		setActiveLayers(newLayers);

		if (newLayers.includes(layersEnum.activeFire.value)) {
			fetchActiveFires().then((data) => {
				setActiveFiresGeoJson(data);
			});
		} else {
			// Remove the layer
			setActiveFiresGeoJson(null);
		}
	};

	let loginOffsetTop = 50;

	if (!!markerCoords.length) {
		loginOffsetTop += 180;
	}

	const handleClickOnFire = ({ coordinates, feature }) => {
		const { properties } = feature;

		setPopup(
			<Popup
				coordinates={coordinates}
				offset={{
					'bottom-left': [12, -38],
					'bottom': [0, -38],
					'bottom-right': [-12, -38]
				}}
			>
				<FirePopupContent
					properties={properties}
					hide={() => setPopup(null)}
				/>
			</Popup>
		);
	};

	const markers = activeFiresGeoJson?.features?.map((feature) => {
		const coordinates = [
			feature.properties.irwin_InitialLongitude,
			feature.properties.irwin_InitialLatitude
		];

		return (
			<Marker
				key={coordinates}
				coordinates={coordinates}
				anchor="bottom"
				onClick={() => handleClickOnFire({
					coordinates,
					feature
				})}
			>
				<FireMarker />
			</Marker>
		);
	});

	return (
		<div style={{ position: "relative", height: "100vh" }}>
			<MapSearch
				style={{
					position: "absolute",
					zIndex: 100,
					maxWidth: "466px",
					top: "20px",
					left: "50%",
					marginLeft: "-150px"
				}}
				flyTo={flyTo}
				value={selectedAddress}
			/>

			<FireRiskAlert
				isOpen={!!markerCoords.length}
				fireRiskValue={fireRiskValue}
				setActiveLayers={handleLayerChange}
				activeLayers={activeLayers}
			/>

			<LoginAlert
				style={{
					top: `${loginOffsetTop}px`,
					left: "10px",
				}}
				onClick={setVisibilityToModal}
			/>

			<Map
				ref={mapRef}
				style={mapboxMapStyle}
				center={mapCenter}
				zoom={[currentZoom]}
				minZoom={MAPBOX_DEFAULT_MIN_ZOOM}
				containerStyle={{
					height: `100vh`,
					width: '100%',
				}}
				onClick={onMapClick}
				onStyleLoad={onStyleLoad}
				onZoom={onZoom}
			>
				<Source onSource id="source_id" tileJsonSource={RASTER_SOURCE_OPTIONS} />
				{markers}


				{popup}

				{activeLayers.includes(layersEnum.wildFireAi.value) ? (
					<React.Fragment>
						<Source
							id="source_id"
							tileJsonSource={RASTER_SOURCE_OPTIONS}
						/>

						<Layer
							type="raster"
							id="layer_id"
							sourceId="source_id"
							paint={{ "raster-opacity": 0.55 }}
						/>
					</React.Fragment>
				) : null}

				{activeFiresGeoJson ? (
					<React.Fragment>
						<GeoJSONLayer
							data={activeFiresGeoJson}
							linePaint={{
								"line-color": "#000",
								"line-width": 2
							}}
						/>
					</React.Fragment>

				) : null}


				{markerCoords.length ? (
					<Marker
						coordinates={markerCoords}
						anchor="bottom"
						style={{ width: "50px", height: "63px", zIndex: 0 }}
					>
						<img width="100%" alt="marker" src="/images/icons/marker.png" />
					</Marker>
				) : null}

				<SwitchControl {...switchControlContent} changeMapStyle={changeMapStyle} />

				<MapLegend />
			</Map>

			<Modal
				opened={modalIsVisible}
				onClose={() => setModalIsVisible(false)}
				onSubmit={onSavePoint}
				error={error}
				selectedAddress={selectedAddress}
			/>

			{!!markerCoords.length ? (
				// <RangeSlider
				// 	style={{
				// 		position: "absolute",
				// 		bottom: "0",
				// 		zIndex: 9,
				// 		width: "98%"
				// 	}}
				// 	onChange={loadRaster}
				// 	fireRiskValue={fireRiskValue}
				// />
				<TimeSeriesChart timeseriesFireRiskData={timeseriesFireRiskData} onTimeseriesPointClick={onTimeseriesPointClick} isTimeseriesHidden={isTimeseriesHidden}
					setIsTimeseriesHidden={setIsTimeseriesHidden} />
			) : null}
		</div>
	);
};

export default MapboxMap;
